import { useEffect, useRef, useState } from 'react';
import {
  Card,
  Typography,
  Tag,
  Space,
  Spin,
  Empty,
  Button,
  Progress,
  Divider,
} from 'antd';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import request from '@/utils/request';
import { formatCents, formatDate } from '@/utils/format';
import { thumb } from '@/utils/image';
import type { CreatorBundleRunDTO } from '@zero/shared';

const STATUS_COLOR: Record<string, string> = {
  pending: 'default',
  running: 'processing',
  success: 'success',
  failed: 'error',
};

const UsageDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { runId } = useParams<{ runId: string }>();
  const [run, setRun] = useState<CreatorBundleRunDTO | null>(null);
  const [urlMap, setUrlMap] = useState<Map<number, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = async () => {
    try {
      const res = await request.get<CreatorBundleRunDTO>(`/creator/bundle-runs/${runId}`);
      const data = res.data as unknown as CreatorBundleRunDTO;
      setRun(data);
      const ids = data.records.flatMap((r) => r.outputImageIds);
      if (ids.length > 0) {
        const imgRes = await request.get<{ items: { id: number; accessUrl: string }[] }>(
          `/creator/images?ids=${ids.join(',')}`,
        );
        const map = new Map<number, string>();
        for (const im of (imgRes.data as unknown as { items: { id: number; accessUrl: string }[] }).items) {
          map.set(im.id, im.accessUrl);
        }
        setUrlMap(map);
      }
      return data;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [runId]);

  useEffect(() => {
    if (!run) return;
    if (run.status === 'success' || run.status === 'failed') {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    if (!pollRef.current) {
      pollRef.current = setInterval(() => {
        load().then((d) => {
          if (d && (d.status === 'success' || d.status === 'failed')) {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
          }
        });
      }, 2000);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.status]);

  if (loading || !run) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin /></div>;
  }

  const done = run.records.filter((r) => r.status === 'success').length;
  const total = run.records.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/usage')}>
            返回列表
          </Button>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {run.bundleTitle}
          </Typography.Title>
          <Tag color={STATUS_COLOR[run.status]} style={{ margin: 0 }}>
            {run.status === 'running' ? '生成中' : run.status === 'success' ? '已完成' : run.status === 'failed' ? '失败' : '准备中'}
          </Tag>
        </Space>
        <Button icon={<ReloadOutlined />} onClick={() => load()}>刷新</Button>
      </Space>

      <Card>
        <Space style={{ justifyContent: 'space-between', width: '100%', marginBottom: 12 }}>
          <Space direction="vertical" size={4}>
            <Typography.Text type="secondary">进度</Typography.Text>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {done} / {total}
            </Typography.Title>
          </Space>
          <Space direction="vertical" size={4} style={{ textAlign: 'right' }}>
            <Typography.Text type="secondary">
              扣费 {formatCents(run.chargedCents)}
              {run.refundedCents > 0 && ` · 退款 ${formatCents(run.refundedCents)}`}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              开始于 {run.startedAt ? formatDate(run.startedAt) : '—'}
            </Typography.Text>
          </Space>
        </Space>
        <Progress
          percent={pct}
          status={run.status === 'failed' ? 'exception' : run.status === 'success' ? 'success' : 'active'}
          strokeColor={{ from: '#F59E0B', to: '#D97706' }}
        />
        {run.errorMessage && (
          <Typography.Text type="danger" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
            {run.errorMessage}
          </Typography.Text>
        )}
      </Card>

      <Card title="生成结果">
        {run.records.length === 0 ? (
          <Empty className="brand-empty" description="没有记录" />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {run.records.map((rec, i) => (
              <div
                key={rec.id}
                style={{
                  padding: 12,
                  borderRadius: 10,
                  background: '#FDFCF9',
                  border: '1px solid #F0EBDE',
                }}
              >
                <Space style={{ justifyContent: 'space-between', width: '100%', marginBottom: 8 }}>
                  <Space>
                    <Typography.Text strong>第 {i + 1} 步</Typography.Text>
                    <Tag color={STATUS_COLOR[rec.status]} style={{ margin: 0 }}>
                      {rec.status}
                    </Tag>
                  </Space>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {rec.completedAt ? `完成于 ${formatDate(rec.completedAt)}` : rec.startedAt ? '生成中…' : '等待中'}
                  </Typography.Text>
                </Space>
                {rec.errorMessage && (
                  <Typography.Text type="danger" style={{ fontSize: 12 }}>
                    {rec.errorMessage}
                  </Typography.Text>
                )}
                {rec.outputImageIds.length > 0 && (
                  <div
                    style={{
                      marginTop: 8,
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, 1fr)',
                      gap: 8,
                    }}
                  >
                    {rec.outputImageIds.map((imgId) => (
                      <div
                        key={imgId}
                        style={{
                          aspectRatio: '1/1',
                          borderRadius: 8,
                          overflow: 'hidden',
                          background: '#f1f5f9',
                        }}
                      >
                        {urlMap.has(imgId) ? (
                          <img
                            src={thumb(urlMap.get(imgId), 200)}
                            alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#94A3B8',
                              fontSize: 12,
                            }}
                          >
                            #{imgId}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </Space>
        )}
        <Divider style={{ margin: '16px 0 8px' }} />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          试跑记录，用于自测发布前的最终效果
        </Typography.Text>
      </Card>
    </Space>
  );
};

export default UsageDetailPage;
